import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, money, shopToday } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const METHODS = ["CASH", "BANK TRANSFER", "PAYID", "CARD DEBIT", "CARD"];

export const LoanLedger = ({ docId }) => {
  const [pos, setPos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [entry, setEntry] = useState({
    amount: "",
    method: "CASH",
    kind: "interest",
    note: "",
    entry_date: shopToday(),
  });

  const load = useCallback(() => {
    api
      .get(`/documents/${docId}/loan-position`)
      .then(({ data }) => setPos(data))
      .catch(() => setPos(null));
  }, [docId]);

  useEffect(load, [load]);

  const record = async () => {
    if (!(Number(entry.amount) > 0)) {
      toast.error("Enter the amount the customer paid");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${docId}/loan-repayment`, {
        amount: Number(entry.amount),
        method: entry.method,
        kind: entry.kind,
        note: entry.note,
        entry_date: entry.entry_date.split("-").reverse().join("/"),
      });
      setPos(data);
      setEntry({
        amount: "",
        method: "CASH",
        kind: "interest",
        note: "",
        entry_date: shopToday(),
      });
      toast.success(
        data.status === "repaid" ? "Loan settled in full" : "Repayment recorded"
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!pos) return null;

  return (
    <section className="no-print border border-border p-5" data-testid="loan-ledger">
      <h2 className="text-base font-bold uppercase tracking-wider md:text-lg">
        Loan position today
      </h2>
      <div className="mt-4 grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-3 xl:grid-cols-6">
        {[
          ["Principal out", money(pos.principal_outstanding), "principal"],
          [`Interest (${pos.months_elapsed} mth)`, money(pos.interest_accrued), "interest"],
          ["Interest paid", money(pos.interest_paid), "interest-paid"],
          ["Monthly due", money(pos.monthly_due), "monthly"],
          ["Total due now", money(pos.total_due), "total"],
          ["Due date", pos.end_date || "-", "due-date"],
        ].map(([label, value, key]) => (
          <div key={key} className="bg-white p-4" data-testid={`loan-pos-${key}`}>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              {label}
            </div>
            <div className="tabular mt-2 text-base font-bold">{value}</div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground" data-testid="loan-basis-note">
        Interest is charged by the calendar month on the principal still outstanding,
        at {pos.interest_rate}% a month, with a minimum of one month and nothing
        accruing past the {pos.months_term}-month term.
      </p>
      {pos.interest_stopped && (
        <p className="mt-2 text-sm font-semibold text-[#8A6A22]" data-testid="loan-interest-stopped">
          Interest has stopped at the {pos.months_term}-month term — the agreement is
          past its due date and no further interest is being added.
        </p>
      )}
      {pos.interest_overpaid_applied > 0 && (
        <p className="mt-2 text-sm" data-testid="loan-overpaid-applied">
          {money(pos.interest_overpaid_applied)} was paid above the interest accrued and
          has come off the principal.
        </p>
      )}
      {pos.overdue && (
        <p className="mt-3 text-sm font-semibold text-[#A11B1B]" data-testid="loan-overdue">
          This loan is past its due date — {Math.abs(pos.days_left)} days overdue.
        </p>
      )}

      {pos.status === "active" && (
        <div className="mt-5 border-t border-border pt-4">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Record a repayment
          </Label>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input
              type="date"
              max={shopToday()}
              className="h-10 w-[165px] rounded-none"
              value={entry.entry_date}
              onChange={(e) => setEntry({ ...entry, entry_date: e.target.value })}
              title="The day the customer actually paid — change it if you are entering it late"
              data-testid="loan-repay-date"
            />
            <Input
              type="number"
              step="0.01"
              placeholder="Amount"
              className="h-10 w-32 rounded-none text-right"
              value={entry.amount}
              onChange={(e) => setEntry({ ...entry, amount: e.target.value })}
              data-testid="loan-repay-amount"
            />
            <Select value={entry.kind} onValueChange={(v) => setEntry({ ...entry, kind: v })}>
              <SelectTrigger className="h-10 w-[190px] rounded-none" data-testid="loan-repay-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-white">
                <SelectItem value="interest">Monthly interest</SelectItem>
                <SelectItem value="principal">Off the principal</SelectItem>
              </SelectContent>
            </Select>
            <Select value={entry.method} onValueChange={(v) => setEntry({ ...entry, method: v })}>
              <SelectTrigger className="h-10 w-[170px] rounded-none" data-testid="loan-repay-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-white">
                {METHODS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={() => setEntry({ ...entry, amount: pos.monthly_due })}
              variant="outline"
              className="h-10 rounded-none"
              data-testid="loan-repay-fill-monthly"
            >
              Monthly amount
            </Button>
            <Button
              onClick={record}
              disabled={busy}
              className="h-10 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
              data-testid="loan-repay-save"
            >
              Record repayment
            </Button>
          </div>
        </div>
      )}

      {pos.repayments?.length > 0 && (
        <div className="mt-5 overflow-x-auto border border-border">
          <table className="w-full text-sm" data-testid="loan-repayments-table">
            <thead className="bg-[#F4F1EA]">
              <tr>
                {["Date", "Type", "Method", "Amount", "Taken by"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {pos.repayments.map((r, i) => (
                <tr key={i} data-testid={`loan-repayment-${i}`}>
                  <td className="tabular px-3 py-2">
                    {r.entry_date}
                    {r.recorded_on && (
                      <span
                        className="ml-2 text-xs text-muted-foreground"
                        data-testid={`loan-repayment-late-${i}`}
                      >
                        (entered {r.recorded_on})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 capitalize">{r.kind}</td>
                  <td className="px-3 py-2">{r.method}</td>
                  <td className="tabular px-3 py-2">{money(r.amount)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.staff}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
